import { Select, Spin } from 'antd';
import { Fragment, useMemo, useRef, useCallback, useEffect } from 'react';
import { debounce } from 'shared/utils/common.utils';
import { useMergeState } from 'shared/hooks';

interface RemoteSelectProps<T> {
  fetchData: (params: { search: string; page: number; excludeIds?: number[] }) => Promise<T[]>;
  onChange?: (value: number | null, option?: T) => void;
  value?: number | null;
  placeholder?: string;
  style?: React.CSSProperties;
  optionKey?: keyof T;
  optionLabel?: keyof T;
  pageSize?: number;
  customRenderOption?: (option: T) => React.ReactNode;
  loading?: boolean;
  allowClear?: boolean;
  isSelect?: boolean;
  excludeIds?: number[];
  status?: '' | 'error' | 'warning';
  isOptionDisabled?: (option: T) => boolean;
  fetchOnMount?: boolean;
  hideOptionsWhileLoading?: boolean;
  disabled?: boolean;
  preloadedOptions?: T[];
}

interface SelectState<T> {
  options: T[];
  searchValue: string;
  page: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  error: string | null;
}

function RemoteSelect<T>({
  fetchData,
  onChange,
  value,
  placeholder,
  style,
  optionKey = 'id' as keyof T,
  optionLabel = 'name' as keyof T,
  pageSize = 20,
  customRenderOption,
  loading,
  allowClear = true,
  isSelect,
  excludeIds = [],
  status,
  isOptionDisabled,
  fetchOnMount = false,
  hideOptionsWhileLoading = true,
  disabled,
  preloadedOptions = [],
}: RemoteSelectProps<T>) {
  const [state, setMergedState] = useMergeState<SelectState<T>>({
    options: [],
    searchValue: '',
    page: 1,
    hasMore: true,
    isLoadingMore: false,
    error: null,
  });

  const isInitialFetchDone = useRef(false);
  const latestRequest = useRef<symbol>();
  const hasFetchedOnMount = useRef(false);
  const hasRunResetOnce = useRef(false);

  useEffect(() => {
    if (fetchOnMount && !hasRunResetOnce.current) {
      hasRunResetOnce.current = true;
      return;
    }
    hasRunResetOnce.current = true;
    reset();
  }, [isSelect, JSON.stringify(excludeIds)]);

  /**
   * Call the API to get options data based on search, pagination, and excludeIds.
   */
  const fetch = useCallback(
    async (search: string, pageNum: number = 1) => {
      const requestId = Symbol();
      latestRequest.current = requestId;
      setMergedState({ isLoadingMore: true, error: null });
      try {
        const result = await fetchData({ search, page: pageNum, excludeIds });
        if (latestRequest.current === requestId) {
          const newOptions = pageNum === 1 ? result : [...state.options, ...result];
          const uniqueOptions = Array.from(new Map(newOptions.map((item) => [item[optionKey], item])).values());
          setMergedState({
            options: uniqueOptions,
            hasMore: result.length >= pageSize,
            page: pageNum,
          });
        }
      } catch (e) {
        if (latestRequest.current === requestId) {
          setMergedState({
            hasMore: false,
            error: e instanceof Error ? e.message : 'Échec du chargement des données',
          });
        }
        throw e;
      } finally {
        if (latestRequest.current === requestId) {
          setMergedState({ isLoadingMore: false });
        }
      }
    },
    [fetchData, optionKey, pageSize, state.options, setMergedState, excludeIds],
  );

  useEffect(() => {
    if (!fetchOnMount || hasFetchedOnMount.current) return;
    hasFetchedOnMount.current = true;
    isInitialFetchDone.current = true;
    fetch('', 1);
  }, [fetchOnMount, fetch]);

  /**
   * Debounce fetch function to reduce API calls on user input.
   */
  const debouncedFetch = useMemo(() => {
    const debounced = debounce((value: string) => {
      setMergedState({ searchValue: value, page: 1 });
      fetch(value, 1);
      setTimeout(() => {
        const dropdown = document.querySelector('.ant-select-dropdown .rc-virtual-list-holder');
        if (dropdown) {
          dropdown.scrollTop = 0;
        }
      }, 100);
    }, 300);
    return debounced;
  }, [fetch, setMergedState]);
  /**
   * Trigger fetch for more data when the user scrolls to the bottom of the dropdown.
   */
  const handleScroll = async (e: React.UIEvent<HTMLDivElement>) => {
    if (!state.hasMore || state.isLoadingMore) return;
    const target = e.target as HTMLDivElement;
    console.log('target.scrollHeight', target.scrollHeight, 'target.clientHeight', target.clientHeight);
    if (target.scrollHeight > target.clientHeight) {
      const isBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 1;
      if (isBottom) {
        const nextPage = state.page + 1;
        await fetch(state.searchValue, nextPage);
      }
    }
  };

  /**
   * Handle user selection.
   */
  const handleChange = useCallback(
    async (value: number | null) => {
      if (value == null) {
        onChange?.(null, undefined);
        return;
      }
      const selected = mergedOptions.find((item) => item[optionKey] === value);
      onChange?.(value, selected);
    },
    [onChange, state.options, preloadedOptions, optionKey],
  );

  /**
   * Reset the component to its initial state and fetch page 1 again.
   */
  const reset = useCallback(() => {
    setMergedState({
      options: [],
      searchValue: '',
      page: 1,
      hasMore: true,
      isLoadingMore: false,
      error: null,
    });
    fetch('');
  }, [fetch, setMergedState]);

  const derivedLabel = useMemo(() => {
    if (value == null) return undefined;
    const allKnownOptions = [...state.options, ...preloadedOptions];
    const found = allKnownOptions.find((item) => (item[optionKey] as unknown as number) === (value as number));
    if (found) {
      return String(found[optionLabel] ?? '').replace(/<[^>]+>/g, '');
    }
    return String(value);
  }, [value, state.options, preloadedOptions, optionKey, optionLabel]);

  const mergedOptions = state.options;

  /**
   * Customize dropdown content: include loading spinner, error message, or no data message.
   */
  const dropdownRender = (menu: React.ReactElement) => (
    <Fragment>
      {mergedOptions.length > 0 ? (
        menu
      ) : (
        <div style={{ padding: 8, textAlign: 'center', color: '#888' }}>
          {state.isLoadingMore ? <Spin className='select-spinner' /> : 'Aucune option disponible'}
        </div>
      )}
      {mergedOptions.length > 0 && state.isLoadingMore && (
        <div style={{ padding: 15, textAlign: 'center' }}>
          <Spin className='select-spinner' />
        </div>
      )}
      {state.error && <div style={{ padding: 8, textAlign: 'center', color: 'red' }}>{state.error}</div>}
    </Fragment>
  );

  const isInitialLoading = state.isLoadingMore && state.page === 1;
  const valueExistsInOptions =
    value != null &&
    (state.options.some((opt) => (opt[optionKey] as unknown as number) === (value as number)) ||
      preloadedOptions.some((opt) => (opt[optionKey] as unknown as number) === (value as number)));
  const displayValue =
    hideOptionsWhileLoading && isInitialLoading && !valueExistsInOptions
      ? undefined
      : value != null
        ? { value: value as number, label: derivedLabel }
        : undefined;

  return (
    <Select
      showSearch
      labelInValue
      allowClear={allowClear}
      placeholder={placeholder}
      style={style}
      value={displayValue}
      optionLabelProp='label'
      status={status}
      onChange={(val) => {
        if (val) {
          handleChange(val.value);
        } else {
          handleChange(null);
        }
      }}
      onSearch={(value) => {
        debouncedFetch(value);
      }}
      onPopupScroll={handleScroll}
      onFocus={() => {
        if (!isInitialFetchDone.current) {
          debouncedFetch('');
          isInitialFetchDone.current = true;
        }
      }}
      filterOption={false}
      dropdownRender={dropdownRender}
      loading={loading}
      disabled={disabled}
    >
      {mergedOptions.map((item) => (
        <Select.Option
          key={item[optionKey] as string}
          value={item[optionKey] as number}
          label={String(item[optionLabel] ?? '').replace(/<[^>]+>/g, '')}
          className='fade-in-option'
          disabled={isOptionDisabled?.(item)}
        >
          {customRenderOption ? (
            customRenderOption(item)
          ) : (
            <span dangerouslySetInnerHTML={{ __html: String(item[optionLabel] ?? '') }} />
          )}
        </Select.Option>
      ))}
    </Select>
  );
}

export default RemoteSelect;
